import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Search, CheckCircle2, Heart, Clock, AlertTriangle, ShieldCheck, ShieldOff, History, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const STATUS_META = {
  active:    { bg: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", Icon: CheckCircle2 },
  sponsored: { bg: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300", Icon: Heart },
  trial:     { bg: "bg-sky-500/15 text-sky-700 dark:text-sky-300", Icon: Clock },
  lapsed:    { bg: "bg-destructive/15 text-destructive", Icon: AlertTriangle },
};

export default function AdminPage() {
  const { user, subscription } = useAuth();
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState({});
  const [q, setQ] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Hard guard: if not an admin, redirect home.
  if (user && !subscription?.is_admin) return <Navigate to="/" replace />;

  const setStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.put(`/admin/users/${id}/subscription`, { status });
      toast.success(`Set to ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const toggleAdmin = async (id, makeAdmin) => {
    if (!makeAdmin && !confirm("Revoke admin rights from this user? They'll lose access to this page.")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.put(`/admin/users/${id}/admin-flag`, { is_admin: makeAdmin });
      toast.success(makeAdmin ? "Promoted to admin" : "Admin rights revoked");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const filtered = users.filter((u) =>
    !q ||
    u.email?.toLowerCase().includes(q.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(q.toLowerCase()),
  );

  return (
    <div className="space-y-5" data-testid="admin-page">
      <header>
        <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
          <Shield className="w-8 h-8 text-fuchsia-500" /> Admin
        </h1>
        <p className="text-muted-foreground">Grant sponsorship to deserving accounts. They'll get the full app for free.</p>
      </header>

      <Card className="rounded-3xl p-4 border-2">
        <div className="flex items-center gap-2 px-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…"
                 className="border-0 focus-visible:ring-0 min-h-[44px]" data-testid="admin-search" />
          <span className="text-xs text-muted-foreground whitespace-nowrap">{filtered.length} / {users.length}</span>
        </div>
      </Card>

      <Card className="rounded-3xl border-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider font-bold text-muted-foreground">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Trial ends</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const meta = STATUS_META[u.status] || STATUS_META.lapsed;
                const PillIcon = meta.Icon;
                const isBusy = !!busy[u.id];
                return (
                  <tr key={u.id} className="border-t hover:bg-muted/30" data-testid={`admin-row-${u.id}`}>
                    <td className="p-3">
                      <div className="font-bold">{u.full_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="p-3 capitalize">{u.role}{u.is_admin && <span className="ml-1 text-[10px] uppercase tracking-wider rounded-full bg-fuchsia-500/20 text-fuchsia-600 px-2 py-0.5 font-bold">Admin</span>}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${meta.bg}`}>
                        <PillIcon className="w-3 h-3" /> {u.status}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {u.status !== "sponsored" && (
                        <Button size="sm" onClick={() => setStatus(u.id, "sponsored")} disabled={isBusy}
                                className="rounded-full font-bold gap-1 mr-1"
                                style={{ background: "hsl(287 64% 53%)", color: "white" }}
                                data-testid={`sponsor-${u.id}`}>
                          <Heart className="w-3 h-3" /> Sponsor
                        </Button>
                      )}
                      {u.status === "sponsored" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "active")} disabled={isBusy}
                                className="rounded-full font-bold mr-1"
                                data-testid={`unsponsor-${u.id}`}>Un-sponsor</Button>
                      )}
                      {u.status !== "active" && u.status !== "sponsored" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "active")} disabled={isBusy}
                                className="rounded-full font-bold mr-1"
                                data-testid={`activate-${u.id}`}>Activate</Button>
                      )}
                      {!u.is_admin && (
                        <Button size="sm" variant="outline" onClick={() => toggleAdmin(u.id, true)} disabled={isBusy}
                                className="rounded-full font-bold gap-1 border-2"
                                data-testid={`make-admin-${u.id}`}>
                          <ShieldCheck className="w-3 h-3" /> Make admin
                        </Button>
                      )}
                      {u.is_admin && u.id !== user?.id && (
                        <Button size="sm" variant="outline" onClick={() => toggleAdmin(u.id, false)} disabled={isBusy}
                                className="rounded-full font-bold gap-1 border-2 text-destructive"
                                data-testid={`revoke-admin-${u.id}`}>
                          <ShieldOff className="w-3 h-3" /> Revoke admin
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="5" className="p-10 text-center text-muted-foreground">No matching users.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AuditLogCard />
    </div>
  );
}


// ===================================================================
// Audit Log card — shows recent admin actions, newest first.
// ===================================================================
const ACTION_META = {
  subscription_change: { label: "Subscription change", Icon: Heart, color: "text-fuchsia-600" },
  admin_promote:       { label: "Promoted to admin",   Icon: ShieldCheck, color: "text-emerald-600" },
  admin_revoke:        { label: "Admin revoked",       Icon: ShieldOff,   color: "text-rose-600" },
  account_self_delete: { label: "Account deleted",     Icon: Trash2,      color: "text-rose-600" },
};

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return d.toLocaleDateString();
}

function AuditLogCard() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/audit-log", { params: { limit: 200 } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  };

  useEffect(() => { if (open && rows === null) load(); }, [open]);

  return (
    <Card className="rounded-3xl p-5 border-2" data-testid="audit-log-card">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 -m-1 p-1"
        data-testid="audit-log-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-client/12 ring-1 ring-client/25 grid place-items-center">
            <History className="w-5 h-5 text-client" />
          </div>
          <div className="text-left">
            <h2 className="font-display font-extrabold text-lg leading-tight">Audit log</h2>
            <p className="text-xs text-muted-foreground">Recent admin actions: sponsorships, promotions, deletions.</p>
          </div>
        </div>
        <span className="text-sm font-bold text-client">{open ? "Hide" : "Show"} →</span>
      </button>

      {open && (
        <div className="mt-4 max-h-[420px] overflow-auto rounded-2xl border" data-testid="audit-log-list">
          {rows === null ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No admin actions recorded yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider">
                <tr><th className="text-left p-3">When</th><th className="text-left p-3">Action</th><th className="text-left p-3">Who</th><th className="text-left p-3">Target</th><th className="text-left p-3">Details</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = ACTION_META[r.action] || { label: r.action, Icon: History, color: "text-muted-foreground" };
                  const Icon = meta.Icon;
                  return (
                    <tr key={r.id} className="border-t" data-testid={`audit-row-${r.id}`}>
                      <td className="p-3 whitespace-nowrap text-muted-foreground" title={r.created_at}>{relTime(r.created_at)}</td>
                      <td className="p-3"><span className={`inline-flex items-center gap-1.5 font-bold ${meta.color}`}><Icon className="w-3.5 h-3.5" /> {meta.label}</span></td>
                      <td className="p-3"><b>{r.actor_name || r.actor_email || "—"}</b></td>
                      <td className="p-3"><b>{r.target_name || r.target_email || "—"}</b><div className="text-xs text-muted-foreground">{r.target_email}</div></td>
                      <td className="p-3 text-xs text-muted-foreground">
                        {r.action === "subscription_change" && r.extra
                          ? <><span className="font-mono">{r.extra.from}</span> → <span className="font-mono font-bold text-foreground">{r.extra.to}</span></>
                          : r.action === "account_self_delete" && r.extra?.deleted_collections
                            ? `purged ${Object.values(r.extra.deleted_collections).reduce((a,b)=>a+b,0)} rows`
                            : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}
